import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Check,
  ChevronDown,
  ChevronLeft,
  Code2,
  Copy,
  CornerDownLeft,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link2,
  List,
  ListOrdered,
  ListTodo,
  Pencil,
  Quote,
  RefreshCw,
  Sparkles,
  Strikethrough,
  Type,
  Underline,
  Unlink,
  X,
} from 'lucide-react';
import OryaThinkingStatus from '../../documentation/components/OryaThinkingStatus';
import { aiWriteNotice } from '../services/vcNoticeApi';
import { useClampedFixedPanel } from '../utils/noticeFloatingPanel';
import CustomersNoticeTip from './CustomersNoticeTip';
import './CustomersNoticeFmtBar.css';

const THINKING_STEPS = [
  { text: 'Orya está pensando…', duration: 2000 },
  { text: 'Analisando o texto…', duration: 2500 },
  { text: 'Refinando a escrita…', duration: 2500 },
  { text: 'Gerando resposta…', duration: null },
];

const EDIT_ACTIONS = [
  { id: 'improve', label: 'Melhorar a escrita', prompt: 'Melhorar a escrita' },
  { id: 'grammar', label: 'Corrigir ortografia e gramática', prompt: 'Corrigir ortografia e gramática' },
  { id: 'longer', label: 'Tornar mais longo', prompt: 'Tornar mais longo' },
  { id: 'shorter', label: 'Tornar mais curto', prompt: 'Tornar mais curto' },
  { id: 'simple', label: 'Simplificar a escrita', prompt: 'Simplificar a escrita' },
  { id: 'summarize', label: 'Resumir', prompt: 'Resumir' },
];

function wordCount(text) {
  return String(text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function hasMultipleWords(text) {
  return wordCount(text) > 1;
}

export default function CustomersNoticeFmtBar({
  position,
  selectedText = '',
  linkHref = '',
  aiOnly = false,
  keepOpenSelector = '',
  aiWrite = aiWriteNotice,
  onExec,
  onReplaceSelection,
  onInsertBelow,
  onClose,
}) {
  const linkInputRef = useRef(null);
  const promptRef = useRef(null);
  const [menu, setMenu] = useState(null); // list | transform | align | link | edit | result
  const [linkUrl, setLinkUrl] = useState('');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [lastPrompt, setLastPrompt] = useState('');
  const draft = useMemo(() => String(selectedText || '').trim(), [selectedText]);
  const canImprove = hasMultipleWords(draft);
  const hasLink = Boolean(String(linkHref || '').trim());

  const panelTop = position?.top || 0;
  const panelLeft = position?.left || 16;
  const hideFmtBar = menu === 'result' || menu === 'edit';

  const fmtPreferred = useMemo(
    () => ({ top: panelTop, left: panelLeft }),
    [panelTop, panelLeft]
  );
  const oryaPreferred = useMemo(
    () => ({ top: panelTop, left: panelLeft }),
    [panelTop, panelLeft]
  );
  const linkPreferred = useMemo(
    () => ({ top: panelTop + (hideFmtBar ? 0 : 40), left: panelLeft }),
    [panelTop, panelLeft, hideFmtBar]
  );
  const { ref: rootRef, coords: fmtCoords } = useClampedFixedPanel(fmtPreferred, [menu]);
  const { ref: oryaRef, coords: oryaCoords } = useClampedFixedPanel(oryaPreferred, [
    menu,
    suggestion,
    loading,
    error,
  ]);
  const { ref: linkRef, coords: linkCoords } = useClampedFixedPanel(linkPreferred, [menu, hasLink]);

  useEffect(() => {
    if (hasLink && menu === 'link') {
      setLinkUrl(String(linkHref || '').trim());
    }
  }, [hasLink, linkHref, menu]);

  useEffect(() => {
    const onDoc = (e) => {
      if (e.target?.closest?.('.cmc-notice-fmt, .cmc-notice-fmt-orya, .cmc-notice-fmt-link-pop')) {
        return;
      }
      // Não fechar no mousedown dentro do editor — senão a re-seleção fica intermitente
      if (e.target?.closest?.('.cmc-manual-editor')) return;
      if (keepOpenSelector && e.target?.closest?.(keepOpenSelector)) return;
      onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (menu) setMenu(null);
        else onClose?.();
      }
    };
    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu, onClose, keepOpenSelector]);

  useEffect(() => {
    if (menu === 'link') {
      const t = window.setTimeout(() => linkInputRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
    if (menu === 'edit' || menu === 'result') {
      const t = window.setTimeout(() => promptRef.current?.focus(), 40);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [menu]);

  const runAi = useCallback(
    async (userPrompt) => {
      const p = String(userPrompt || '').trim();
      if (!p || !draft || loading) return;
      setLoading(true);
      setError('');
      setSuggestion('');
      setLastPrompt(p);
      setMenu('result');
      try {
        const data = await aiWrite({ prompt: p, draft });
        setSuggestion(String(data?.content || '').trim());
      } catch (e) {
        setError(e.message || 'Falha ao gerar com a Orya.');
        setSuggestion('');
      } finally {
        setLoading(false);
      }
    },
    [draft, loading, aiWrite]
  );

  const onMelhorar = (e) => {
    e.preventDefault();
    if (!canImprove) return;
    setPrompt('');
    runAi('Melhorar a escrita');
  };

  const onEditOpen = (e) => {
    e.preventDefault();
    setMenu((m) => (m === 'edit' ? null : 'edit'));
    setError('');
  };

  const applyLink = () => {
    const url = String(linkUrl || '').trim();
    if (!url) return;
    onExec?.('createLink', url);
    setMenu(null);
    setLinkUrl('');
  };

  return (
    <>
      {!hideFmtBar ? (
      <div
        ref={rootRef}
        className="cmc-notice-fmt"
        style={{ top: fmtCoords.top, left: fmtCoords.left }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <CustomersNoticeTip
          label={
            canImprove
              ? 'Melhorar com Orya'
              : 'Selecione mais de uma palavra para melhorar'
          }
          placement="top"
        >
          <button
            type="button"
            className={'cmc-notice-fmt-ai' + (canImprove ? '' : ' is-disabled')}
            disabled={!canImprove || loading}
            onClick={onMelhorar}
          >
            <Sparkles size={14} strokeWidth={1.9} aria-hidden />
            Melhorar
          </button>
        </CustomersNoticeTip>
        <CustomersNoticeTip label="Editar com Orya" placement="top">
          <button type="button" className="cmc-notice-fmt-ai" onClick={onEditOpen}>
            <Sparkles size={14} strokeWidth={1.9} aria-hidden />
            Edit
          </button>
        </CustomersNoticeTip>

        {!aiOnly ? (
        <>
        <span className="cmc-notice-fmt-sep" aria-hidden />

        <div className="cmc-notice-fmt-dd">
          <button
            type="button"
            className={'cmc-notice-fmt-icon-btn' + (menu === 'list' ? ' is-open' : '')}
            aria-label="Listas"
            onClick={(e) => {
              e.preventDefault();
              setMenu((m) => (m === 'list' ? null : 'list'));
            }}
          >
            <List size={15} strokeWidth={1.85} />
            <ChevronDown size={12} strokeWidth={2} />
          </button>
          {menu === 'list' ? (
            <div className="cmc-notice-fmt-menu cmc-notice-fmt-menu--icons">
              <CustomersNoticeTip label="Lista com marcadores" placement="top">
                <button type="button" onClick={() => onExec?.('insertUnorderedList')}>
                  <List size={16} strokeWidth={1.85} />
                </button>
              </CustomersNoticeTip>
              <CustomersNoticeTip label="Lista numerada" placement="top">
                <button type="button" onClick={() => onExec?.('insertOrderedList')}>
                  <ListOrdered size={16} strokeWidth={1.85} />
                </button>
              </CustomersNoticeTip>
              <CustomersNoticeTip label="Citação" placement="top">
                <button type="button" onClick={() => onExec?.('formatBlock', 'BLOCKQUOTE')}>
                  <Quote size={16} strokeWidth={1.85} />
                </button>
              </CustomersNoticeTip>
              <CustomersNoticeTip label="Lista de tarefas" placement="top">
                <button type="button" onClick={() => onExec?.('insertUnorderedList')}>
                  <ListTodo size={16} strokeWidth={1.85} />
                </button>
              </CustomersNoticeTip>
            </div>
          ) : null}
        </div>

        <div className="cmc-notice-fmt-dd">
          <button
            type="button"
            className={'cmc-notice-fmt-text-btn' + (menu === 'transform' ? ' is-open' : '')}
            onClick={(e) => {
              e.preventDefault();
              setMenu((m) => (m === 'transform' ? null : 'transform'));
            }}
          >
            Texto
            <ChevronDown size={12} strokeWidth={2} />
          </button>
          {menu === 'transform' ? (
            <div className="cmc-notice-fmt-menu cmc-notice-fmt-menu--list">
              <div className="cmc-notice-fmt-menu-title">Transformar em</div>
              {[
                { label: 'Texto', cmd: 'formatBlock', val: 'P', icon: 'T' },
                { label: 'Cabeçalho 1', cmd: 'formatBlock', val: 'H1', icon: 'H₁' },
                { label: 'Cabeçalho 2', cmd: 'formatBlock', val: 'H2', icon: 'H₂' },
                { label: 'Cabeçalho 3', cmd: 'formatBlock', val: 'H3', icon: 'H₃' },
                { label: 'Cabeçalho 4', cmd: 'formatBlock', val: 'H4', icon: 'H₄' },
              ].map((it) => (
                <button
                  key={it.label}
                  type="button"
                  className="cmc-notice-fmt-menu-row"
                  onClick={() => onExec?.(it.cmd, it.val)}
                >
                  <span className="cmc-notice-fmt-menu-ico">{it.icon}</span>
                  <span>{it.label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <span className="cmc-notice-fmt-sep" aria-hidden />

        <button
          type="button"
          className="cmc-notice-fmt-icon-btn"
          aria-label="Negrito"
          onClick={() => onExec?.('bold')}
        >
          <Bold size={15} strokeWidth={2.2} />
        </button>
        <button
          type="button"
          className="cmc-notice-fmt-icon-btn"
          aria-label="Itálico"
          onClick={() => onExec?.('italic')}
        >
          <Italic size={15} strokeWidth={2.2} />
        </button>
        <button
          type="button"
          className="cmc-notice-fmt-icon-btn"
          aria-label="Sublinhado"
          onClick={() => onExec?.('underline')}
        >
          <Underline size={15} strokeWidth={2.2} />
        </button>
        <button
          type="button"
          className="cmc-notice-fmt-icon-btn"
          aria-label="Tachado"
          onClick={() => onExec?.('strikeThrough')}
        >
          <Strikethrough size={15} strokeWidth={2.2} />
        </button>
        <button
          type="button"
          className="cmc-notice-fmt-icon-btn"
          aria-label="Código"
          onClick={() => onExec?.('formatBlock', 'PRE')}
        >
          <Code2 size={15} strokeWidth={1.85} />
        </button>

        <div className="cmc-notice-fmt-dd">
          <button
            type="button"
            className={'cmc-notice-fmt-icon-btn' + (menu === 'align' ? ' is-open' : '')}
            aria-label="Alinhamento"
            onClick={(e) => {
              e.preventDefault();
              setMenu((m) => (m === 'align' ? null : 'align'));
            }}
          >
            <AlignLeft size={15} strokeWidth={1.85} />
            <ChevronDown size={12} strokeWidth={2} />
          </button>
          {menu === 'align' ? (
            <div className="cmc-notice-fmt-menu cmc-notice-fmt-menu--list">
              <button
                type="button"
                className="cmc-notice-fmt-menu-row"
                onClick={() => onExec?.('justifyLeft')}
              >
                <AlignLeft size={15} strokeWidth={1.85} />
                <span>Alinhar à esquerda</span>
              </button>
              <button
                type="button"
                className="cmc-notice-fmt-menu-row"
                onClick={() => onExec?.('justifyCenter')}
              >
                <AlignCenter size={15} strokeWidth={1.85} />
                <span>Alinhar no centro</span>
              </button>
              <button
                type="button"
                className="cmc-notice-fmt-menu-row"
                onClick={() => onExec?.('justifyRight')}
              >
                <AlignRight size={15} strokeWidth={1.85} />
                <span>Alinhar à direita</span>
              </button>
              <div className="cmc-notice-fmt-menu-hr" />
              <button
                type="button"
                className="cmc-notice-fmt-menu-row"
                onClick={() => onExec?.('indent')}
              >
                <IndentIncrease size={15} strokeWidth={1.85} />
                <span>Aumentar o recuo</span>
              </button>
              <button
                type="button"
                className="cmc-notice-fmt-menu-row"
                onClick={() => onExec?.('outdent')}
              >
                <IndentDecrease size={15} strokeWidth={1.85} />
                <span>Diminuir o recuo</span>
              </button>
            </div>
          ) : null}
        </div>

        <CustomersNoticeTip label={hasLink ? 'Editar link' : 'Inserir link'} placement="top">
          <button
            type="button"
            className={
              'cmc-notice-fmt-icon-btn' +
              (menu === 'link' ? ' is-open' : '') +
              (hasLink ? ' is-active-link' : '')
            }
            aria-label={hasLink ? 'Editar link' : 'Link'}
            onClick={(e) => {
              e.preventDefault();
              setMenu((m) => (m === 'link' ? null : 'link'));
              setLinkUrl(hasLink ? String(linkHref || '').trim() : '');
            }}
          >
            {hasLink ? <Pencil size={15} strokeWidth={1.85} /> : <Link2 size={15} strokeWidth={1.85} />}
          </button>
        </CustomersNoticeTip>
        {hasLink ? (
          <CustomersNoticeTip label="Remover link" placement="top">
            <button
              type="button"
              className="cmc-notice-fmt-icon-btn cmc-notice-fmt-icon-btn--danger"
              aria-label="Remover link"
              onClick={(e) => {
                e.preventDefault();
                onExec?.('unlink');
              }}
            >
              <Unlink size={15} strokeWidth={1.85} />
            </button>
          </CustomersNoticeTip>
        ) : null}
        </>
        ) : null}
      </div>
      ) : null}

      {menu === 'link' && !aiOnly ? (
        <div
          ref={linkRef}
          className="cmc-notice-fmt-link-pop"
          style={{ top: linkCoords.top, left: linkCoords.left }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <input
            ref={linkInputRef}
            className="cmc-notice-fmt-link-input"
            value={linkUrl}
            placeholder="Cole um link e pressione enter"
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
            }}
          />
          {hasLink ? (
            <CustomersNoticeTip label="Remover link" placement="top">
              <button
                type="button"
                className="cmc-notice-fmt-icon-btn cmc-notice-fmt-icon-btn--danger"
                aria-label="Remover link"
                onClick={() => onExec?.('unlink')}
              >
                <Unlink size={14} strokeWidth={2} />
              </button>
            </CustomersNoticeTip>
          ) : null}
          <button
            type="button"
            className="cmc-notice-fmt-icon-btn"
            aria-label="Fechar"
            onClick={() => setMenu(null)}
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      ) : null}

      {menu === 'edit' ? (
        <div
          ref={oryaRef}
          className="cmc-notice-fmt-orya"
          style={{ top: oryaCoords.top, left: oryaCoords.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="cmc-notice-fmt-orya-head">
            <Sparkles size={16} strokeWidth={1.9} className="cmc-notice-fmt-orya-mark" />
            <div>
              <div className="cmc-notice-fmt-orya-title">Como posso melhorar a sua escrita?</div>
              <div className="cmc-notice-fmt-orya-sub">
                Posso refinar sua escrita, corrigir a gramática e muito mais.
              </div>
            </div>
          </div>
          <div className="cmc-notice-fmt-orya-input-row">
            <input
              ref={promptRef}
              className="cmc-notice-fmt-orya-input"
              value={prompt}
              placeholder="Peça à Orya para editar ou escrever"
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  runAi(prompt);
                }
              }}
            />
            <button
              type="button"
              className="cmc-notice-fmt-orya-send"
              disabled={!prompt.trim() || !draft || loading}
              onClick={() => runAi(prompt)}
            >
              <CornerDownLeft size={14} strokeWidth={2} />
            </button>
          </div>
          <div className="cmc-notice-fmt-orya-actions">
            {EDIT_ACTIONS.map((a) => (
              <button
                key={a.id}
                type="button"
                className="cmc-notice-fmt-orya-action"
                disabled={!draft || loading}
                onClick={() => runAi(a.prompt)}
              >
                <Type size={14} strokeWidth={1.85} />
                <span>{a.label}</span>
                <CornerDownLeft size={12} strokeWidth={2} className="cmc-notice-fmt-orya-enter" />
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {menu === 'result' ? (
        <div
          ref={oryaRef}
          className="cmc-notice-fmt-orya cmc-notice-fmt-orya--result"
          style={{ top: oryaCoords.top, left: oryaCoords.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="cmc-notice-fmt-orya-head cmc-notice-fmt-orya-head--row">
            <div className="cmc-notice-fmt-orya-brand">
              <Sparkles size={15} strokeWidth={1.9} />
              <strong>Orya</strong>
            </div>
            <button
              type="button"
              className="cmc-notice-fmt-icon-btn"
              onClick={() => {
                setMenu(null);
                setSuggestion('');
                setError('');
                setLoading(false);
                onClose?.();
              }}
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>
          <div className="cmc-notice-fmt-orya-suggestion">
            {loading ? (
              <OryaThinkingStatus active steps={THINKING_STEPS} />
            ) : (
              suggestion || error || 'Sem sugestão.'
            )}
          </div>
          <div className="cmc-notice-fmt-orya-feedback">
            <CustomersNoticeTip label="Gerar novamente" placement="top">
              <button
                type="button"
                className="cmc-notice-fmt-icon-btn"
                disabled={loading}
                onClick={() => runAi(lastPrompt || 'Melhorar a escrita')}
              >
                <RefreshCw size={14} strokeWidth={1.85} />
              </button>
            </CustomersNoticeTip>
          </div>
          <div className="cmc-notice-fmt-orya-input-row">
            <input
              ref={promptRef}
              className="cmc-notice-fmt-orya-input"
              value={prompt}
              placeholder="Peça à Orya para editar ou escrever"
              disabled={loading}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!loading) runAi(prompt);
                }
              }}
            />
            <button
              type="button"
              className="cmc-notice-fmt-orya-send"
              disabled={!prompt.trim() || loading}
              onClick={() => runAi(prompt)}
            >
              <CornerDownLeft size={14} strokeWidth={2} />
            </button>
          </div>
          <div className="cmc-notice-fmt-orya-actions">
            <button
              type="button"
              className="cmc-notice-fmt-orya-action is-active"
              disabled={!suggestion || loading}
              onClick={() => {
                onReplaceSelection?.(suggestion);
                onClose?.();
              }}
            >
              <Check size={14} strokeWidth={2} />
              <span>Substituir por esta resposta</span>
              <CornerDownLeft size={12} strokeWidth={2} className="cmc-notice-fmt-orya-enter" />
            </button>
            <button
              type="button"
              className="cmc-notice-fmt-orya-action"
              disabled={!suggestion || loading}
              onClick={() => {
                onInsertBelow?.(suggestion);
                onClose?.();
              }}
            >
              <IndentIncrease size={14} strokeWidth={1.85} />
              <span>Inserir abaixo</span>
            </button>
            <button
              type="button"
              className="cmc-notice-fmt-orya-action"
              disabled={!suggestion || loading}
              onClick={() => {
                if (suggestion) navigator.clipboard?.writeText(suggestion);
              }}
            >
              <Copy size={14} strokeWidth={1.85} />
              <span>Copiar</span>
            </button>
            <button
              type="button"
              className="cmc-notice-fmt-orya-action"
              disabled={loading}
              onClick={() => {
                setMenu('edit');
                setSuggestion('');
                setError('');
              }}
            >
              <ChevronLeft size={14} strokeWidth={2} />
              <span>Voltar</span>
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
